import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import PayrollHeaderInterface from '@app/Interface/Report/PayrollHeaderInterface';
import PayrollDetails from '@app/Components/Report/Payroll/Details';
import { reactFormatter } from 'react-tabulator';
import { Text } from '@patternfly/react-core';
import ItemLoader from '@app/Components/ItemLoader';
import moment from "moment-timezone";

import SCTabulator from '@app/utils/SCTabulator';
// TODO: Eventually we'll want to our SCTabulatorCommonOptions, but only AFTER it properly exposes both the default common options object
// and the function to generate options w/ persistent movable columns
import TabulatorCommonOptions from '@app/utils/TabulatorCommonOptions';

interface PayrollListReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    setReportTableRef: React.Dispatch<React.SetStateAction<React.MutableRefObject<null> | null>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    loading: boolean;
}


const PayrollListReport: React.FC<PayrollListReportProps> = ({ pageId, setLastUpdatedText, setReportTableRef, setLoading, loading }) => {
    // In order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const { userState } = React.useContext(UserContext);

    const payrollTableRef = React.useCallback(node => { return {current: node} }, []);;

    const [reportData, setReportData] = React.useState<PayrollHeaderInterface[]>([]);
    const [selectedStatement, setSelectedStatement] = React.useState<number | null>(null);

    const SelectPayrollLink = (props: any) => {
        const payrollId = props.cell.getValue();
        const payrollIdInt: number = Number.parseInt(payrollId);
        return (
            <a href="#" onClick={e => { e.preventDefault(); setSelectedStatement(payrollIdInt) }}>
                {payrollIdInt}
            </a>
        );
    };

    // TODO: Will we eventually add search? Requirements say that's a future phase.
    const tabulatorColumns = [
        { title: 'Payroll ID', field: 'HEADER_ID', formatter: reactFormatter(<SelectPayrollLink />) },
        { title: 'Month-Year', field: 'POST_DATE', formatter: (cell: any, formatterParams: any, onRendered: any): string => {
            const postDate: string = cell.getValue();
            const batchName = cell.getData().BATCH_NAME ?? '';
            let monthYear = '';
            if (batchName.includes('Set')) {
                monthYear = moment(postDate).subtract(1, 'months').format('MMMM - YYYY');
            } else if (batchName.includes('Adv')) {
                monthYear = moment(postDate).format('MMMM - YYYY');
            }
            return monthYear;
        } },
        { title: 'Type', field: 'BATCH_NAME', formatter: (cell: any, formatterParams: any, onRendered: any): string => {
            const batchName: string = cell.getValue();
            let type = '';
            if (batchName.includes('Set')) {
                type = 'Settlement';
            } else if (batchName.includes('Adv')) {
                type = 'Advance';
            }
            return type;
        } },
        { title: 'Post Date', field: 'POST_DATE', formatter: 'datetime', formatterParams: {
            outputFormat: 'MM/DD/YYYY'
        } },
    ];

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        if (!selectedStatement) {
            ReportDataHelper
                .getTableUpdatedText(controller, 'AP_MPIS_TRANSFER_HEADERS')
                .then(text => { if (mounted) setLastUpdatedText(text) });
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [selectedStatement]);

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        async function fetchReportData() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await ReportDataHelper.getPayrollListData(controller, userState.user.currentFpuId, pageId);
                if (mounted) {
                    setReportData(rows);
                    setLoading(false);
                }
            } else {
                if (mounted) {
                    setReportData([]);
                    setLoading(false);
                }
            }
        }

        setLoading(true);
        fetchReportData();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id, userState.user.currentFpuId]);

    if (selectedStatement) {
        return <PayrollDetails
            pageId={pageId}
            payrollId={selectedStatement}
            setLastUpdatedText={setLastUpdatedText}
            clearSelectedPayroll={() => setSelectedStatement(null)}
        />;
    }

    if (loading) {
        return <ItemLoader />;
    }

    if (reportData.length === 0) {
        return <p><em>No report data to show for the selected producer</em></p>;
    }

    return (
        <>
            <Text className="pf-u-mb-lg">Click on the Payroll ID to view the Payroll Stub for the Transaction.</Text>
            <SCTabulator
                tableRef={payrollTableRef}
                data={reportData}
                columns={tabulatorColumns}
                layout='fitColumns'
                options={TabulatorCommonOptions}
            />
        </>
    );
};

export default PayrollListReport;